import type { ReactNode } from 'react';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import type { FailureAction, FailureCopy } from './failure-copy';
import { endSession } from './session-ended';

type ActionId = Exclude<FailureAction, 'none'>;

interface Props {
  copy: FailureCopy;
  /** What each action id does; the button is drawn only when the copy's action has a handler. `sign-in` ends the session unless given. */
  handlers?: Partial<Record<ActionId, () => void>>;
  /** The button is disabled (a retry in flight). */
  busy?: boolean;
  className?: string;
  /** Extra content after the body (the permissions a 403 names). */
  children?: ReactNode;
}

/** The one inline state for a failure: title, body, action. `role=alert` while something failed, `status` otherwise. */
export function FailureAlert({ copy, handlers, busy, className, children }: Props) {
  const onAction = copy.action && ({ 'sign-in': endSession, ...handlers } as Partial<Record<ActionId, () => void>>)[copy.action.id];
  const showAction = !!onAction;
  return (
    <Alert
      tone={copy.tone}
      title={copy.title}
      role={copy.tone === 'danger' ? 'alert' : 'status'}
      className={className}
      actions={
        showAction ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={onAction}>
            {copy.action!.label}
          </Button>
        ) : undefined
      }
    >
      {copy.body}
      {children}
    </Alert>
  );
}
